import { useActivityInformationCompanyQuery } from '../api/useActivityInformationCompanyQuery';
import { Box, Flex, Heading, Text, useDisclosure } from '@chakra-ui/react';
import { ActivityInformationBox } from '../shared/activityInformationBox';
import { InformationAlertDisplay } from '../shared/informationAlertDisplay';
import { getFullAddressString } from '../../../../util/formatters/addressFormatters';
import { faBank } from '@fortawesome/free-solid-svg-icons';
import { BaseCard, BaseDialog, BaseIcon, BaseSkeletonStack, formatCurrency } from '@cks-internal/react-base';
import { EntityNoteQueryType } from '../../../../enums/common/EntityNoteQueryType';
import { EntityNotesWarning } from '../../../../features/entityNotes/EntityNotesWarning';
import { EntityNoteType } from '../../../../enums/common/EntityNoteType';
import { EntityNotesDisplay } from '../../../../features/entityNotes/EntityNotesDisplay';
import { useEntityNotesQuery } from '../../../../features/entityNotes/apis/useEntityNotesQuery';
import { useActivityHotelSearchInformationQuery } from '../../../../features/inbox/api/useActivityHotelSearchInformationQuery';
import { useCompanyProjectsQuery } from '../../../../features/activities/companyProjects/api/useCompanyProjectsQuery';
import { CompanyProjectDisplay } from '../../../../features/activities/companyProjects/CompanyProjectDisplay';
import { FooterFlsqBadge } from '../shared/FooterFlsqBadge';

type Props = {
  companyId: number;
  activityId?: number;
  flsq?: number;
  showFlsq?: boolean;
};
/**
 * ActivityInformationCompanyDisplay component fetches and renders company-specific activity information.
 *
 * Responsibilities:
 * - Queries company details using `useActivityInformationCompanyQuery` with the provided companyId.
 * - Renders nothing if no data is returned.
 * - Once data is available, shows a card (`BaseCard`) with:
 *   - Company name in the header.
 *   - Full address string below the name.
 *   - Optional GDS card last four digits if present.
 * - Renders an entity-level warning via `EntityNotesWarning` for Code Red notes.
 * - Displays informational alerts for Code Red notes and Agent Notes.
 * - Constructs a two-column layout of various policy and note fields`.
 * - Includes a "Forms Of Payment" box when payment methods or attached files are available, with download links.
 *
 * @component
 * @param {{ companyId: number }} props - Component props
 * @param {number} props.companyId - The unique identifier of the company to display information for.
 * @returns {JSX.Element} The rendered company information card or an empty fragment.
 */
export const ActivityInformationCompanyDisplay = (props: Props) => {
  const query = useActivityInformationCompanyQuery(props.companyId);
  const notesDialogDisclosure = useDisclosure();

  // Query to check if notes exist
  const notesQuery = useEntityNotesQuery(
    {
      EntityNoteQueryType: EntityNoteQueryType.Company,
      Active: true,
      EntityNoteTypes: [EntityNoteType.AgentNote],
      IsPinned: true
    },
    props.companyId
  );

  // Activity context data for company projects
  const hotelSearchInfo = useActivityHotelSearchInformationQuery(props.activityId);
  const companyProjectsQuery = useCompanyProjectsQuery(
    hotelSearchInfo.data?.PreferredCity ? [hotelSearchInfo.data.PreferredCity] : undefined,
    undefined,
    hotelSearchInfo.data?.CheckInDate,
    hotelSearchInfo.data?.CheckOutDate,
    props.companyId
  );

  if (query.isLoading) {
    return <BaseSkeletonStack />;
  }
  if (!query.data) {
    return <></>;
  }

  const hasAutomatedApprovalNotes: boolean = !!query.data?.AutomatedApprovalSystemNotes;

  return (
    <BaseCard
      size={'sm'}
      height={'100%'}
      header={
        <Box>
          <Flex alignItems='center' justifyContent='space-between' mb={'2px'}>
            <Heading>{query.data.CompanyName}</Heading>
            <FooterFlsqBadge flsq={props.flsq} />
          </Flex>
          <Box fontSize={'sm'}>{getFullAddressString(query.data.Address)}</Box>
          {query.data.GdsCardLastFour && (
            <BaseIcon toolTip={'GDS Card On File'} icon={faBank}>
              <Box> GDS Card On File: {query.data.GdsCardLastFour}</Box>
            </BaseIcon>
          )}
        </Box>
      }>
      {props.companyId && (
        <EntityNotesWarning
          entityIds={[props.companyId]}
          queryType={EntityNoteQueryType.Company}
          noteType={EntityNoteType.CodeRed}
        />
      )}

      {/* Notes Alert - Clickable to open dialog */}
      {props.companyId && notesQuery.data && notesQuery.data.length > 0 && (
        <Box cursor='pointer' onClick={notesDialogDisclosure.onOpen} _hover={{ opacity: 0.8 }}>
          <InformationAlertDisplay
            toDisplay={['Click to view all company notes']}
            status={'info'}
            header={'Company Notes'}
          />
        </Box>
      )}

      <Flex direction={'column'} gap={'12px'}>
        {/* Legacy Alert Display for Quick Reference */}
        <Flex flexDir={'column'} gap={'4px'}>
          <InformationAlertDisplay toDisplay={query.data.CodeRedNotes} status={'error'} header={'Code Red'} />
          <InformationAlertDisplay toDisplay={query.data.AgentNotes} status={'warning'} header={'Agent Notes'} />

          {/* Policy Information Section */}
          <Box>
            <Text fontSize={'sm'} fontWeight={'bold'} mb={2} color={'blue.600'}>
              Company Policies:
            </Text>
            <Box h={'fit-content'} w='100%' css={{ columnCount: [2], columnGap: '8px', gap: '8px' }}>
              {hasAutomatedApprovalNotes && (
                <ActivityInformationBox
                  header={'Automated Approval System Enabled'}
                  htmlText={query.data.AutomatedApprovalSystemNotes}
                />
              )}
              {!hasAutomatedApprovalNotes && (
                <>
                  <ActivityInformationBox header={'Approval'} htmlText={query.data.ApprovalProcess} />
                  <ActivityInformationBox header={'Auth. Booker Policy'} htmlText={query.data.AuthorizedBookerPolicy} />
                </>
              )}
              <ActivityInformationBox header={'Amenity Notes'} htmlText={query.data.AmenityNotes} />
              <ActivityInformationBox
                header={'Reporting Requirements'}
                htmlText={query.data.ReportingRequirementNotes}
              />
              <ActivityInformationBox header={'Coding Requirements'} htmlText={query.data.CodingRequirements} />

              <ActivityInformationBox header={'Check In Requirements'} htmlText={query.data.CheckInRequirements} />

              <ActivityInformationBox header={'Payment Note'} htmlText={query.data.PaymentNote} />
              <ActivityInformationBox header={'Rooming Policy'} htmlText={query.data.RoomingPolicy} />
              <ActivityInformationBox header={'Travel Policy'} htmlText={query.data.TravelPolicy} />
              <ActivityInformationBox header={'VIP Travel Policy'} htmlText={query.data.VipTravellersPolicy} />

              {/* Service Fee Template Display */}
              {query.data.ServiceFeeTemplate && (
                <ActivityInformationBox header={`Service Fee Template: ${query.data.ServiceFeeTemplate.Name}`}>
                  {query.data.ServiceFeeTemplate.Description && (
                    <Text fontSize={'xs'} color={'gray.600'} mb={2}>
                      {query.data.ServiceFeeTemplate.Description}
                    </Text>
                  )}
                  <Text fontSize={'xs'} mb={2}>
                    <Text as={'span'} fontWeight={'bold'}>
                      Cap:
                    </Text>{' '}
                    {formatCurrency(query.data.ServiceFeeTemplate.ServiceFeeCap)}
                  </Text>
                  {query.data.ServiceFeeTemplate.ServiceFeeRanges &&
                    query.data.ServiceFeeTemplate.ServiceFeeRanges.length > 0 && (
                      <Box>
                        <Text fontSize={'xs'} fontWeight={'bold'} mb={1}>
                          Night Ranges:
                        </Text>
                        {query.data.ServiceFeeTemplate.ServiceFeeRanges.sort(
                          (a, b) => (a.StartNight ?? 0) - (b.StartNight ?? 0)
                        ).map((range, index) => (
                          <Flex key={index} justifyContent={'space-between'} fontSize={'xs'} py={1}>
                            <Text>
                              Night {range.StartNight}
                              {range.EndNight ? ` - ${range.EndNight}` : '+'}
                            </Text>
                            <Text fontWeight={'bold'}>{formatCurrency(range.Amount)}</Text>
                          </Flex>
                        ))}
                      </Box>
                    )}
                  {query.data.ServiceFeeTemplate.Disabled && (
                    <Text fontSize={'xs'} color={'red.500'} fontWeight={'bold'} mt={2}>
                      This template is currently disabled
                    </Text>
                  )}
                </ActivityInformationBox>
              )}
            </Box>
          </Box>
        </Flex>

        {/* Company Projects Display - When Activity Context Available */}
        {props.activityId && companyProjectsQuery.data && companyProjectsQuery.data.length > 0 && (
          <Box mt={4}>
            <Text fontSize={'sm'} fontWeight={'bold'} mb={2} color={'blue.600'}>
              Relevant Company Projects:
            </Text>
            <CompanyProjectDisplay projects={companyProjectsQuery.data} />
          </Box>
        )}
      </Flex>

      {/* Draggable Notes Dialog */}
      <BaseDialog
        dragDialog={{
          initialW: 600,
          initialH: 500
        }}
        disclosure={notesDialogDisclosure}
        header={<Heading>Company Notes</Heading>}>
        <EntityNotesDisplay
          entityId={props.companyId}
          queryType={EntityNoteQueryType.Company}
          noteDisplayTypes={[EntityNoteType.AgentNote]}
          isReadOnly={true}
          isPinned
        />
      </BaseDialog>
    </BaseCard>
  );
};
